// Images attached to a bug report: screen captures, and images pasted, dropped or picked from
// disk. Every image is redrawn on a canvas before it is attached, which normalizes it to PNG
// or JPEG (the two the server stores), drops its metadata (EXIF location and the like) and
// bounds its size so a handful still fit in one report request.

/** Most screenshots one report carries; the server keeps the same cap. */
export const MAX_BUG_SCREENSHOTS = 5;
/** Longest edge, in pixels, an attached image is scaled down to. */
export const MAX_SCREENSHOT_EDGE = 2560;
/** Largest data URL (characters) kept as PNG; past it the image is re-encoded as JPEG. */
export const MAX_SCREENSHOT_CHARS = 2_000_000;

/** The image files in a paste or drop's DataTransfer (clipboard items when it lists no files). */
export function imageFilesFrom(dataTransfer) {
  if (!dataTransfer) return [];
  const isImage = (f) => !!f && typeof f.type === "string" && f.type.startsWith("image/");
  const files = Array.from(dataTransfer.files || []).filter(isImage);
  if (files.length > 0) return files;
  return Array.from(dataTransfer.items || [])
    .filter((it) => it.kind === "file" && it.type.startsWith("image/"))
    .map((it) => it.getAsFile())
    .filter(isImage);
}

/** `width` × `height` scaled down (never up) so the longer edge is at most `maxEdge`. */
export function fitWithin(width, height, maxEdge = MAX_SCREENSHOT_EDGE) {
  const scale = Math.min(1, maxEdge / Math.max(width, height, 1));
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * The canvas as a data URL: PNG when that fits in `maxChars`, else the first JPEG quality
 * that does (the lowest tried when none does).
 */
export function encodeCanvas(canvas, maxChars = MAX_SCREENSHOT_CHARS) {
  let url = canvas.toDataURL("image/png");
  if (url.length <= maxChars) return url;
  for (const quality of [0.85, 0.7, 0.55]) {
    url = canvas.toDataURL("image/jpeg", quality);
    if (url.length <= maxChars) break;
  }
  return url;
}

/** Draw `source` (an image, bitmap or video frame) of the given size onto a fresh canvas. */
export function drawToCanvas(source, srcWidth, srcHeight, doc = document) {
  const { width, height } = fitWithin(srcWidth, srcHeight);
  const canvas = doc.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  // A transparent PNG re-encoded as JPEG would turn black behind its content.
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);
  return canvas;
}

/** An image file (pasted, dropped or picked) as a size-bounded PNG/JPEG data URL. */
export async function imageFileToDataUrl(file) {
  const bitmap = await createImageBitmap(file);
  try {
    return encodeCanvas(drawToCanvas(bitmap, bitmap.width, bitmap.height));
  } finally {
    bitmap.close?.();
  }
}

/**
 * Tracks image attaches still decoding, per form session. `reset` starts a new session (the
 * form opened, or its report was sent): attaches begun before it settle as stale, so their
 * images never land in a later report. `pending` counts the current session's attaches; the
 * form holds its submit while any are pending, so a report never leaves without them.
 */
export function createAttachSessions() {
  let session = 0;
  let pending = 0;
  return {
    reset() {
      session += 1;
      pending = 0;
    },
    /** Start an attach; the token goes back to `end`. */
    begin() {
      pending += 1;
      return session;
    },
    /** Settle an attach; false when its session is over and its images must be dropped. */
    end(token) {
      if (token !== session) return false;
      pending -= 1;
      return true;
    },
    get pending() {
      return pending;
    },
  };
}
